import type { MailboxInfo } from 'owa-client-types';
import getCopilotPrioritizeSettings from 'owa-mail-copilot-settings/lib/selectors/getCopilotPrioritizeSettings';
import isCopilotFeatureEnabled from './isCopilotFeatureEnabled';
import { getIsCopilotUIDisabled } from 'owa-mail-copilot-settings/lib/selectors/getIsCopilotUIDisabled';

/**
 * Determines if Prioritize On Delivery is enabled for the given mailbox (regardless of opt in
 * status or if feature is turned on).
 */
export function isPrioritizeOnDeliveryEnabled(mailboxInfo: MailboxInfo): boolean {
    return (
        (isCopilotFeatureEnabled('Inbox', mailboxInfo, true /* skipLanguageCheck */) ?? false) &&
        !getIsCopilotUIDisabled(mailboxInfo)
    );
}

/**
 * Determines if Prioritize On Delivery (i.e. mail gets prioritized in the service on delivery
 * and doesn't rely on the client) is enabled and also checks if it's been turned on by the
 * user (prioritizationEnabled check).
 */
export function isPrioritizeOnDeliveryOn(mailboxInfo: MailboxInfo): boolean {
    return (
        isPrioritizeOnDeliveryEnabled(mailboxInfo) &&
        getCopilotPrioritizeSettings(mailboxInfo)
            .prioritizationEnabled /* ensures user has setting on */
    );
}

/**
 * Determines if Prioritize On Demand (i.e. click a button and prioritize mail via Copilot)
 * is enabled and turned on.
 */
export function isPrioritizeOnDemandOn(mailboxInfo: MailboxInfo): boolean {
    return (
        (isCopilotFeatureEnabled(
            'InboxPrioritizationOnDemand',
            mailboxInfo,
            true /* skipLanguageCheck */
        ) ??
            false) &&
        !getIsCopilotUIDisabled(mailboxInfo)
    );
}

/**
 * Determines if any version of Prioritize is enabled and turned on.
 */
export function isPrioritizeOn(mailboxInfo: MailboxInfo): boolean {
    return isPrioritizeOnDeliveryOn(mailboxInfo) || isPrioritizeOnDemandOn(mailboxInfo);
}

/**
 * Returns whether the Priority Nudge is enabled for the given mailbox.
 * The nudge is gated on isPrioritizeOnDemandOn AND the user's
 * experimentalSetting not being explicitly set to 'false'.
 */
export function isPriorityNudgeEnabled(mailboxInfo: MailboxInfo): boolean {
    if (!isPrioritizeOnDemandOn(mailboxInfo)) {
        return false;
    }

    const { experimentalSetting } = getCopilotPrioritizeSettings(mailboxInfo);

    // experimentalSetting is null when unset (treated as enabled) or 'true'/'false'
    return experimentalSetting !== 'false';
}
